import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { isEmpty, capitalize, keyBy, lowerCase, groupBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo, useEffect, Children } from 'react'

import { useLazyGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import { useGetDisposalSitesMutation } from 'api/settings/getDisposalSites'
import { HHAlert } from 'components/common/HHAlert'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { getTaxes } from 'components/customer-details/content/work-orders/actions/disposal-ticket/utils'
import { getTransformDisposalData } from 'components/work-order/details/disposal-ticket/settings'
import PreviewField from 'components/work-order/disposal-ticket-dialog/disposal-fee/fields/PreviewField'
import Preview from 'components/work-order/disposal-ticket-dialog/disposal-fee/Preview'
import { getDisposalSiteDetails, getDisposalFeeDetails } from 'components/work-order/disposal-ticket-dialog/disposal-fee/utils'
import T from 'T'
import { formatDateToFEFormat } from 'utils/date'
import { get } from 'utils/lodash'
import { calculateSum, formatCurrency, formatCentsToCurrency } from 'utils/price'

const DisposalTicketListing = ({
  workOrderId,
  configuredServiceId,
  isLoading = false,
  disposalTicketData,
  hideHeader = false,
  noDataMessage = T.DISPOSAL_TICKETS_EMPTY_STATE_MSG,
}) => {
  const theme = useTheme()
  const [getDisposalSites, { data: disposalSites, isLoading: isDisposalSitesLoading }] = useGetDisposalSitesMutation()
  const [getServiceDetails, { data: serviceData, isFetching: isGetServiceDetailsLoading }] = useLazyGetServiceDetailsQuery()

  const isGetLoading = useMemo(
    () => isDisposalSitesLoading || isGetServiceDetailsLoading || isLoading,
    [isDisposalSitesLoading, isGetServiceDetailsLoading, isLoading]
  )
  const allSites = useMemo(() => get(disposalSites, 'sites', []), [disposalSites])
  const hasData = useMemo(() => !isEmpty(disposalTicketData), [disposalTicketData])
  const disposalData = hasData ? getTransformDisposalData(disposalTicketData) : null
  const taxable = useMemo(() => get(disposalData, 'taxable', false), [disposalData])
  const materials = useMemo(() => get(disposalData, 'materials', []), [disposalData])
  const disposalSiteId = useMemo(() => get(disposalData, 'disposalSiteId'), [disposalData])
  const disposalSiteDetails = useMemo(() => getDisposalSiteDetails(disposalSiteId, allSites), [disposalSiteId, allSites])
  const disposalSiteMaterials = useMemo(() => get(disposalSiteDetails, 'disposalSiteMaterial', []), [disposalSiteDetails])
  const disposalSiteMaterialsKeyBy = useMemo(() => keyBy(disposalSiteMaterials, 'id'), [disposalSiteMaterials])
  const allTaxes = useMemo(() => (taxable ? getTaxes(serviceData) : []), [serviceData, taxable])
  const materialDetails = getDisposalFeeDetails(materials, allTaxes, disposalSiteMaterialsKeyBy)

  const tippingFeeSubtotalCents = useMemo(() => calculateSum(materialDetails, 'tippingData.subtotalCents'), [materialDetails])
  const tippingFeeTotalTaxCents = useMemo(() => calculateSum(materialDetails, 'tippingData.totalTaxCents'), [materialDetails])
  const tippingFeeTotalCents = useMemo(
    () => tippingFeeSubtotalCents + tippingFeeTotalTaxCents,
    [tippingFeeSubtotalCents, tippingFeeTotalTaxCents]
  )
  const totalCents = useMemo(() => calculateSum(materialDetails, 'disposalFeeData.subtotalCentsWithTax'), [materialDetails])
  const tippingFeeTaxes = useMemo(() => materialDetails.map(material => material.tippingData.taxes).flat(), [materialDetails])
  const groupTaxes = useMemo(() => groupBy(tippingFeeTaxes, 'id'), [tippingFeeTaxes])
  const effectiveDate = useMemo(() => get(disposalData, 'effectiveDate'), [disposalData])

  useEffect(() => {
    if (workOrderId) {
      getDisposalSites()
    }
  }, [workOrderId, getDisposalSites])

  useEffect(() => {
    if (configuredServiceId) {
      getServiceDetails(configuredServiceId)
    }
  }, [configuredServiceId, getServiceDetails])

  return (
    <Box p={2}>
      {isGetLoading && <LinearProgress sx={{ my: 2 }} />}
      {!isGetLoading && !hasData && (
        <HHAlert borderColor={theme.palette.info.light} severity="info">
          {noDataMessage}
        </HHAlert>
      )}
      {hasData && (
        <>
          {taxable && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Chip label="Taxable" />
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6 }}>
              {!hideHeader && (
                <>
                  <Typography fontWeight={700} variant="h6">
                    {get(disposalData, 'ticketNumber', '')}
                  </Typography>
                  {effectiveDate && <Typography variant="subtitle1">{formatDateToFEFormat(effectiveDate)}</Typography>}
                  <Typography variant="subtitle1">{get(disposalSiteDetails, 'name', '')}</Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
              <Typography variant="h6">{capitalize(T.TIPPING_FEE)}</Typography>
              {Children.toArray(
                materialDetails.map(material => {
                  const { materialType, unitOfMeasure, quantity, unitPriceDollars, tippingData } = material
                  const unit = lowerCase(unitOfMeasure)
                  return (
                    <>
                      <Typography fontWeight={400} my={1} variant="subtitle1">
                        {`${materialType}: ${formatCurrency(unitPriceDollars)} / ${unit} x ${quantity} = ${formatCentsToCurrency(
                          tippingData.subtotalCents
                        )}`}
                      </Typography>
                    </>
                  )
                })
              )}
              <Divider sx={{ my: 2 }} />
              {tippingFeeTotalTaxCents > 0 && (
                <>
                  <PreviewField labelLeft="Subtotal:" labelRight={formatCentsToCurrency(tippingFeeSubtotalCents)} />
                  {Children.toArray(
                    Object.keys(groupTaxes).map(taxId => {
                      const currentGroupTaxes = get(groupTaxes, taxId, [])
                      const taxName = get(currentGroupTaxes, '0.taxName', '')
                      const formattedTaxPrice = get(currentGroupTaxes, '0.formattedTaxPrice', '')
                      const taxAmountCents = calculateSum(currentGroupTaxes, 'taxAmountCents')

                      return (
                        <PreviewField
                          labelLeft={`${taxName} - ${formattedTaxPrice}:`}
                          labelRight={<HHDisplayMoney value={taxAmountCents} />}
                        />
                      )
                    })
                  )}
                </>
              )}
              <PreviewField
                labelLeft="Total:"
                labelRight={formatCentsToCurrency(tippingFeeTotalCents)}
                leftProps={{ variant: 'h6' }}
                mt={1}
                rightProps={{ variant: 'h6' }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography fontWeight={500} variant="h6">
                {capitalize(T.DISPOSAL_FEE)}
              </Typography>
              <Stack direction="column" gap={0.5}>
                {Children.toArray(materialDetails.map((material, index) => <Preview index={index} material={material} />))}
              </Stack>
              <PreviewField
                color="primary.main"
                labelLeft="Total:"
                labelRight={formatCentsToCurrency(totalCents)}
                leftProps={{ variant: 'h6' }}
                mt={1}
                rightProps={{ variant: 'h6' }}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}

DisposalTicketListing.propTypes = {
  workOrderId: PropTypes.string,
  configuredServiceId: PropTypes.string,
  isLoading: PropTypes.bool,
  disposalTicketData: PropTypes.object,
  hideHeader: PropTypes.bool,
  noDataMessage: PropTypes.string,
}

export default DisposalTicketListing
